import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Tooltip } from "bootstrap";
import SearchOverlay from "../pages/SearchOverlay";
import { User, Search, ShoppingCart, ShoppingBag, LogOut } from "lucide-react";
import "../styles/navbar.css";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef();

  const computeCartCount = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const count = cart.reduce((acc, item) => acc + (Number(item.qty) || 1), 0);
    setCartCount(count);
  };

  const handleNavClick = () => {
  setIsMenuOpen(false);
  window.scrollTo(0, 0);
};


  useEffect(() => {
  const tooltipTriggerList = document.querySelectorAll(
    '[data-bs-toggle="tooltip"]'
  );

  const tooltips = [...tooltipTriggerList].map(
    (tooltipEl) => new Tooltip(tooltipEl)
  );

  return () => {
    tooltips.forEach((tooltip) => tooltip.dispose());
  };
}, []);

const hideAllTooltips = () => {
  const tooltipTriggerList = document.querySelectorAll(
    '[data-bs-toggle="tooltip"]'
  );

  tooltipTriggerList.forEach((el) => {
    const instance = Tooltip.getInstance(el);
    if (instance) instance.hide();
  });
};


  useEffect(() => {
    computeCartCount();
    const handleCartUpdated = () => computeCartCount();
    const handleStorage = (e) => {
      if (e.key === "cart") computeCartCount();
    };
    window.addEventListener("cartUpdated", handleCartUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const user = JSON.parse(localStorage.getItem("user")); // logged-in user

  // const handleSignOut = () => {
  //   localStorage.removeItem("user");
  //   setProfileOpen(false);
  //   window.location.reload(); // reload to reset state
  // };

  return (
    <nav className="navbar navbar-expand-md shadow-sm fixed-top">
      <div className="container-fluid d-flex">
        {/* Logo */}
        <NavLink className="navbar-brand d-flex align-items-center" to="/">
          <img
            src={require("../Asset/porvaall-logo.jpg")}
            alt="MagazineSite Logo"
            style={{ height: "40px", width: "auto", objectFit: "contain" }}
          />
        </NavLink>

        {/* Icons on top-right */}
        <div className="d-flex align-items-center order-md-3 navbar-icons gap-3">
          {/* Search Icon */}
          <button
            className="btn"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
            title="Search"
            onClick={() =>{ hideAllTooltips();setShowSearchOverlay(true)}}
          >
            <Search size={20} />
          </button>

          {/* Cart Icon */}
          <div className="ms-auto d-flex align-items-center">
            <NavLink
              to="/cart"
              className="position-relative text-decoration-none text-dark"
              data-bs-toggle="tooltip"
              data-bs-placement="bottom"
              title="Cart"
              onClick={() =>{ hideAllTooltips();window.scrollTo(0, 0)}}
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "0.7rem", padding: "0.25rem 0.5rem" }}
                >
                  {cartCount}
                </span>
              )}
            </NavLink>
          </div>

          {/* Profile Icon with Dropdown */}
          <div className="position-relative" ref={dropdownRef}>
            <button
              className="btn d-flex align-items-center"
              data-bs-toggle="tooltip"
              data-bs-placement="bottom"
              title="Profile"
              onClick={() =>{ hideAllTooltips();setProfileOpen((prev) => !prev)}}
            >
              <User size={20} />
            </button>

            {profileOpen && (
              <div
                className="shadow-sm p-2 bg-white"
                style={{
                  position: "absolute",
                  top: "calc(100% + 15px)",
                  right: 0,
                  minWidth: "180px",
                  borderRadius: "8px",
                  zIndex: 1050,
                }}
              >
                {!user ? (
                  <>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2 mb-2"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate("/login");
                      }}
                    >
                      <User size={16} /> Login
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate("/signup");
                      }}
                    >
                      <User size={16} /> Sign Up
                    </button>
                  </>
                ) : (
                  <>
                    <div className="dropdown-item d-flex align-items-center gap-2 fw-bold mb-2">
                      <User size={16} /> {user.name}
                    </div>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2 mb-2"
                      onClick={() => {
                        window.scrollTo(0, 0);
                        setProfileOpen(false);
                        navigate("/profile"); // <-- route to user profile page
                      }}
                    >
                      <User size={16} /> Profile
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2 mb-2"
                      onClick={() => {
                        window.scrollTo(0, 0);
                        setProfileOpen(false);
                        navigate("/orderhistory");
                      }}
                    >
                      <ShoppingBag size={16} /> Order History
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => {
                        localStorage.removeItem("user");
                        localStorage.removeItem("cart");
                        localStorage.removeItem("addresses");
                        localStorage.setItem("isLoggedIn", "false");
                        window.scrollTo(0, 0);
                        setProfileOpen(false);
                        navigate("/");
                        window.location.reload();
                      }}
                    >
                      <LogOut size={16} /> Sign Out
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        </div>

        {/* Navbar links */}
        <div
          className={`collapse navbar-collapse justify-content-center navbar-custom ${
            isMenuOpen ? "show" : ""
          }`}
        >
          <ul className="navbar-nav mb-2 mb-md-0 text-center">
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
                end
              >
                Home
              </NavLink>
            </li>
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/magazines"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
              >
                Magazines
              </NavLink>
            </li>
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/articles"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
              >
                Books
              </NavLink>
            </li>
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/dailyNews"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
              >
                Daily News
              </NavLink>
            </li>
            {/*
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/stories"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
              >
                Stories
              </NavLink>
            </li>
            */}
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
              >
                About Us
              </NavLink>
            </li>
            <li className="nav-item me-md-2 me-lg-4">
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active-nav" : ""}`
                }
                onClick={handleNavClick}
              >
                Contact Us
              </NavLink>
            </li>
          </ul>
        </div>
      </div>

      <SearchOverlay
        show={showSearchOverlay}
        onClose={() => setShowSearchOverlay(false)}
      />
    </nav>
  );
}
